/**
 * Billing Summary Page - Financial Transactions Surface
 * 
 * PURPOSE: User-facing, auditable representation of the MONEY LEDGER.
 * Tied directly to NFM + R2 archival.
 * 
 * TABLE FIELDS:
 * - Date
 * - Payment method (masked)
 * - What was purchased
 * - Amount
 * - Status
 * - Transaction type
 * 
 * TRANSACTION DETAIL MODAL:
 * - Transaction ID
 * - Settlement timestamps
 * - Product(s) purchased
 * - Amount + currency
 * - Credits issued (reference only)
 * - Receipt resend button
 * 
 * GUARDRAILS:
 * - Read-only
 * - No refunds, no edits, no deletes
 * - Amounts sourced from backend only
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from '@/components/ui/badge';
import { 
  Receipt, Calendar, CreditCard, CheckCircle2, Clock, XCircle,
  Loader2, Mail, ExternalLink, Info, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import AccountNav from '../../components/AccountNav';
import UniversalFooter from '../../components/UniversalFooter';
import { useIsReaderRole } from '../../hooks/useIsReaderRole';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

const BillingPage = () => {
  const navigate = useNavigate();
  const { user, getHomePage } = useAuth();
  const isReader = useIsReaderRole();
  
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState('all');
  
  // Transaction detail modal state
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [resendingReceipt, setResendingReceipt] = useState(false);

  const userId = user?.user_id || user?.id;
  
  // P0.5: Redirect non-Readers away from Billing page
  // Non-Reader roles should never see financial transaction surfaces
  useEffect(() => {
    if (user && !isReader) {
      console.log('[BILLING] Non-Reader role detected, redirecting to profile');
      navigate('/account/profile', { replace: true });
    }
  }, [user, isReader, navigate]);

  // Generate month options (last 12 months)
  const getMonthOptions = () => {
    const options = [{ value: 'all', label: 'All Time' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  // Fetch transactions
  const fetchTransactions = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const monthParam = selectedMonth !== 'all' ? `&month=${selectedMonth}` : '';
      const response = await fetch(
        `${API_URL}/api/nfm/reader/transactions?user_id=${userId}${monthParam}&limit=50`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setTotalTransactions(data.total || 0);
      } else {
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [userId, selectedMonth]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Open transaction detail
  const openTransactionDetail = (txn) => {
    setSelectedTransaction(txn);
    setDetailModalOpen(true);
  };

  // Resend receipt (stubbed via EmailAdapter pattern)
  const handleResendReceipt = async () => {
    if (!selectedTransaction) return;
    
    setResendingReceipt(true);
    try {
      const response = await fetch(
        `${API_URL}/api/nfm/billing/resend-receipt`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          },
          body: JSON.stringify({ 
            transaction_id: selectedTransaction.transaction_id || selectedTransaction.txn_id,
            user_id: userId
          })
        }
      );

      if (response.ok) {
        toast.success('Receipt sent to your email');
      } else {
        // Handle gracefully - endpoint may not exist yet
        const error = await response.json().catch(() => ({}));
        if (response.status === 404) {
          toast.info('Receipt resend coming soon');
        } else {
          toast.error(error.detail || 'Failed to resend receipt');
        }
      }
    } catch (error) {
      console.error('Error resending receipt:', error);
      toast.info('Receipt resend coming soon');
    } finally {
      setResendingReceipt(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format amount
  const formatAmount = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'settled':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-400/40">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Completed
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-400/40">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-400/40">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'refunded':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-400/40">
            Refunded
          </Badge>
        );
      default:
        return (
          <Badge className="bg-slate-500/20 text-slate-400 border-slate-400/40">
            {status}
          </Badge>
        );
    }
  };

  // Get transaction type display
  const getTransactionTypeDisplay = (type) => {
    const typeMap = {
      'bundle_purchase': 'Credit Bundle',
      'subscription_charge': 'Subscription',
      'subscription_renewal': 'Renewal',
      'one_time_purchase': 'Purchase'
    };
    return typeMap[type] || type;
  };

  // Calculate period total
  const periodTotal = transactions
    .filter(t => t.status === 'settled')
    .reduce((sum, t) => sum + (t.type === 'Refund' ? -t.amount_cents : t.amount_cents), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <AccountNav />
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Billing</h1>
          <p className="text-slate-400">
            View your payment history and download receipts
          </p>
        </div>

        {/* Account Navigation Tabs */}
        <div className="mb-6 flex gap-2 border-b border-slate-700 pb-4">
          <Link 
            to="/account/profile"
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            Profile
          </Link>
          <Link 
            to="/account/credits"
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            Reader Credits
          </Link>
          <Link 
            to="/account/billing"
            className="px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 font-medium"
          >
            Billing
          </Link>
          <Link 
            to="/home"
            className="px-4 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
          >
            Reader Home
          </Link>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-slate-400" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700 text-slate-100" data-testid="month-filter">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {getMonthOptions().map(option => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className="text-slate-100 hover:bg-slate-700"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="text-sm text-slate-400">
            {totalTransactions} transaction{totalTransactions !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Transactions Table */}
        <Card className="bg-slate-800/50 border-slate-700" data-testid="billing-summary">
          <CardHeader>
            <CardTitle className="text-white">Payment History</CardTitle>
            <CardDescription className="text-slate-400">
              All charges and payments for your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No transactions found</p>
                <p className="text-sm text-slate-500 mt-2">
                  Your payment history will appear here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {transactions.map((txn) => (
                  <button
                    key={txn.transaction_id || txn.txn_id}
                    onClick={() => openTransactionDetail(txn)}
                    className="w-full flex items-center justify-between py-4 px-2 hover:bg-slate-700/30 transition-colors text-left rounded-lg"
                    data-testid={`transaction-${txn.transaction_id || txn.txn_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          {txn.product_name || getTransactionTypeDisplay(txn.type)}
                        </p>
                        <p className="text-sm text-slate-400">
                          {formatDate(txn.created_at)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {formatAmount(txn.amount_cents)}
                        </p>
                        {getStatusBadge(txn.status)}
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Period Summary */}
        {transactions.length > 0 && (
          <div className="mt-4 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 flex justify-between items-center">
            <span className="text-slate-400">Total for period</span>
            <span className="text-xl font-bold text-white">
              {formatAmount(periodTotal)}
            </span>
          </div>
        )}

        {/* Info Note */}
        <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-sm text-slate-400">
                <strong className="text-slate-300">About Billing:</strong> All transactions are 
                securely processed through Stripe. Click any transaction to view details and 
                resend your receipt.
              </p>
            </div>
          </div>
        </div>
      </div>

      <UniversalFooter />

      {/* Transaction Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          {/* NewAudia Logo */}
          <div className="flex justify-center pt-2 pb-4">
            <img 
              src="/newaudia-logo.jpg" 
              alt="NewAudia™" 
              className="h-12 w-auto rounded"
            />
          </div>
          
          <DialogHeader>
            <DialogTitle className="text-white text-center">Transaction Receipt</DialogTitle>
            <DialogDescription className="text-slate-400 text-center">
              Payment confirmation
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4 py-4">
              {/* Status Badge */}
              <div className="flex justify-center">
                {getStatusBadge(selectedTransaction.status)}
              </div>

              {/* Amount */}
              <div className="text-center">
                <p className="text-3xl font-bold text-white">
                  {formatAmount(selectedTransaction.amount_cents)}
                </p>
                <p className="text-slate-400 text-sm mt-1">
                  {selectedTransaction.product_name || getTransactionTypeDisplay(selectedTransaction.type)}
                </p>
              </div>

              {/* Details Grid */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Date</span>
                  <span className="text-slate-200">
                    {formatDateTime(selectedTransaction.created_at)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Type</span>
                  <span className="text-slate-200">
                    {getTransactionTypeDisplay(selectedTransaction.type)}
                  </span>
                </div>
                {selectedTransaction.credits_issued && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Reader Credits Issued</span>
                    <span className="text-amber-400 font-medium">
                      +{selectedTransaction.credits_issued} RC
                    </span>
                  </div>
                )}
                {selectedTransaction.payment_method && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Payment Method</span>
                    <span className="text-slate-200 capitalize">
                      {selectedTransaction.payment_method}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline"
              onClick={() => setDetailModalOpen(false)}
              className="bg-slate-700 border-slate-600 text-slate-200 hover:bg-slate-600"
            >
              Close
            </Button>
            <Button 
              onClick={handleResendReceipt}
              disabled={resendingReceipt}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              {resendingReceipt ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend Receipt
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BillingPage;
